import { useCallback, useEffect, useRef, useState } from "react";
import { OTP_LENGTH } from "./gstUtils";

interface UseOtpInputResult {
  /** Current OTP digits, always length === OTP_LENGTH */
  otp: string[];
  /** Error message (set by parent via setError) */
  error: string;
  /** Whether all OTP_LENGTH digits are filled */
  isFilled: boolean;
  /** Refs to the individual input elements for focus management */
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  /** Set/clear the error message */
  setError: React.Dispatch<React.SetStateAction<string>>;
  /** Handler for a single box onChange */
  handleChange: (index: number, value: string) => void;
  /** Handler for a single box onKeyDown (Backspace navigation) */
  handleKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Handler for paste on the OTP row */
  handlePaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  /** Reset OTP to empty + clear error */
  reset: () => void;
}

/**
 * Shared OTP input behavior — digit filtering, auto-advance, backspace
 * navigation, and paste-split-into-boxes.
 *
 * Used by both supplier-intelligence and lending/underwriting GST flows.
 */
export function useOtpInput(): UseOtpInputResult {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d*$/.test(value)) return;
      setOtp((prev) => {
        const next = [...prev];
        next[index] = value.slice(-1);
        return next;
      });
      setError("");
      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    []
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [otp]
  );

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (pasted.length) {
      setOtp((prev) => {
        const next = [...prev];
        pasted.split("").forEach((ch, i) => {
          next[i] = ch;
        });
        return next;
      });
      inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    }
  }, []);

  const reset = useCallback(() => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
  }, []);

  return {
    otp,
    error,
    isFilled: otp.join("").length === OTP_LENGTH,
    inputRefs,
    setError,
    handleChange,
    handleKeyDown,
    handlePaste,
    reset,
  };
}